import SEO from '../components/SEO';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { contactAPI } from '../services/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    city: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await contactAPI.create(formData);
      
      toast.success('Message envoyé !', {
        description: result.message || 'Nous vous recontacterons dans les plus brefs délais.',
      });
      
      // Reset form
      setFormData({ name: '', email: '', phone: '', service: '', city: '', message: '' });
    } catch (error) {
      console.error('Error sending contact:', error);
      toast.error('Erreur', {
        description: error.detail || 'Une erreur est survenue. Veuillez réessayer.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Devis gratuit débarras à Annecy | Contactez DEBECO"
        description="Contactez DEBECO pour un devis gratuit de débarras en Savoie, Haute-Savoie et Ain. Réponse sous 24h. Tél : 04 51 40 16 17."
        canonical="/contact"
      />
      {/* Hero - Harmonized */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Contactez-nous</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Obtenez votre devis gratuit en moins de 24h
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="border-2 hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl flex items-center justify-center mb-4">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-lg text-blue-900 mb-2">Téléphone</h3>
                  <a href="tel:0451401617" className="text-emerald-600 hover:text-emerald-700 font-medium">
                    04 51 40 16 17
                  </a>
                  <p className="text-sm text-gray-600 mt-2">Lundi - Samedi : 8h - 19h</p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl flex items-center justify-center mb-4">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-lg text-blue-900 mb-2">Email</h3>
                  <a href="mailto:contact@debeco.fr" className="text-emerald-600 hover:text-emerald-700 font-medium break-all">
                    contact@debeco.fr
                  </a>
                  <p className="text-sm text-gray-600 mt-2">Réponse sous 24h</p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-lg text-blue-900 mb-2">Zones d'intervention</h3>
                  <ul className="text-gray-700 text-sm space-y-1">
                    <li>• Annecy et Lac d'Annecy</li>
                    <li>• Genevois</li>
                    <li>• Montagne</li>
                    <li>• Savoie</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-500">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="w-6 h-6 text-emerald-600" />
                    <h3 className="font-bold text-lg text-blue-900">Intervention rapide</h3>
                  </div>
                  <p className="text-gray-700 text-sm">
                    <strong className="text-emerald-700">48 à 72h</strong> après validation du devis
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-2 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-blue-900 mb-6">Demander un devis gratuit</h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                          Nom complet <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors"
                          placeholder="Jean Dupont"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors"
                          placeholder="jean.dupont@email.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                          Téléphone <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors"
                          placeholder="06 12 34 56 78"
                        />
                      </div>

                      <div>
                        <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                          Type de débarras <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="service"
                          name="service"
                          required
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors"
                        >
                          <option value="">Sélectionnez un service</option>
                          <option value="maison">Débarras de maison</option>
                          <option value="appartement">Débarras d'appartement</option>
                          <option value="cave-grenier">Débarras cave/grenier/garage</option>
                          <option value="professionnel">Débarras professionnel</option>
                          <option value="succession">Débarras succession</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                        Ville <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        required
                        value={formData.city}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors"
                        placeholder="Annecy, Chambéry, Annemasse..."
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        Votre message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        rows="5"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors resize-none"
                        placeholder="Décrivez votre besoin : type de bien, volume estimé, accès, délai souhaité..."
                      ></textarea>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white text-lg py-6 shadow-lg"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                          Envoi en cours...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="w-5 h-5" />
                          Envoyer ma demande
                        </span>
                      )}
                    </Button>

                    <div className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <p>Vos données sont sécurisées et ne seront jamais partagées avec des tiers.</p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Maillage interne */}
      <section className="py-12 bg-gray-50" data-testid="maillage-interne">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">Besoin d'aide ?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/faq" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Questions fréquentes</p>
              <p className="text-sm text-gray-600 mt-1">Tarifs, délais, déroulement de l'intervention</p>
            </Link>
            <Link to="/devis" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Estimation gratuite en ligne</p>
              <p className="text-sm text-gray-600 mt-1">Obtenez un devis en quelques minutes</p>
            </Link>
            <Link to="/services" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Nos services de débarras</p>
              <p className="text-sm text-gray-600 mt-1">Maison, appartement, cave, succession</p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
